import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  ChevronRight,
  Waves,
  Building2,
  Sun,
  Coffee,
  Wine,
  Sparkles,
  BedDouble,
  Utensils,
  Users,
  Ticket,
  ExternalLink,
  Anchor,
  Info,
} from "lucide-react";
import { formatXOF } from "../../lib/i18n";

/**
 * Staff — Nouvelle réservation (iter-73).
 *
 * Replaces the ad-hoc custom form with a two-step picker that then hands off
 * to the SAME `BookingTunnel` used by the customer front office. This
 * guarantees the staff always sees the identical flow, respects blocked
 * dates, room stock and payment rules — with zero drift risk when a new
 * offer is added to the catalog.
 *
 * Flow: pick Universe → pick Offer → navigate('/booking/{id}?source=staff').
 */

// ═══════════════════════════════════════════════════════════════════════
// UNIVERSE CONFIG — mirrors UniverseDashboard + StaffOffersPricing
// ═══════════════════════════════════════════════════════════════════════
const UNIVERSES = [
  {
    id: "hotel",
    label: "Hôtel",
    tagline: "Séjours résidentiels & expériences privatisables",
    icon: Building2,
    offer_ids: ["hebergement", "le_kaai", "lounge", "salles", "seminaire", "spa_wellness"],
  },
  {
    id: "beach_life",
    label: "Beach Life",
    tagline: "Expériences à la journée & événements",
    icon: Waves,
    offer_ids: ["pass_day", "sunset", "brunch", "offres_loisirs", "special_event"],
  },
];

const OFFER_ICONS = {
  hebergement: BedDouble,
  le_kaai: Utensils,
  lounge: Wine,
  salles: Users,
  seminaire: Users,
  spa_wellness: Sparkles,
  pass_day: Sun,
  sunset: Anchor,
  brunch: Coffee,
  offres_loisirs: Waves,
  special_event: Ticket,
};

export default function StaffNewBooking() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [events, setEvents] = useState([]);
  const [universeId, setUniverseId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/offers"),
      api.get("/staff/special-events"),
    ])
      .then(([oRes, eRes]) => {
        setOffers(oRes.data || []);
        const pub = (eRes.data?.items || []).filter(
          (e) => e.status === "published",
        );
        setEvents(pub);
      })
      .catch(() => toast.error("Erreur de chargement des offres"))
      .finally(() => setLoading(false));
  }, []);

  const universe = useMemo(
    () => UNIVERSES.find((u) => u.id === universeId),
    [universeId],
  );

  const displayedOffers = useMemo(() => {
    if (!universe) return [];
    const catalog = offers.filter((o) => universe.offer_ids.includes(o.id));
    // Attach published special events on Beach Life universe.
    if (universe.id === "beach_life" && events.length) {
      catalog.push(
        ...events.map((e) => ({
          id: `special_event:${e.id}`,
          _event_id: e.id,
          _is_event: true,
          name_fr: e.title || e.label || "Événement spécial",
          schedule_fr:
            e.subtitle || e.event_dates?.[0] || "Événement spécial · Beach Life",
          price_adult: e.price_adult || 0,
          price_child: e.price_child || 0,
          hero: e.hero_image || null,
        })),
      );
    }
    return catalog;
  }, [universe, offers, events]);

  const openTunnel = (o) => {
    // Route parity: catalog offers → /booking/{id}, published events → /booking/special-event/{id}
    let target;
    if (o._is_event) {
      target = `/booking/special-event/${o._event_id}?source=staff`;
    } else {
      target = `/booking/${o.id}?source=staff`;
    }
    navigate(target);
  };

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-[1400px] mx-auto" data-testid="staff-new-booking">
      <div className="mb-8">
        <h1 className="font-display-serif text-3xl md:text-4xl text-[#0A0A0A]">Nouvelle réservation</h1>
        <p className="text-sm text-[#0A0A0A]/60 mt-1.5 max-w-2xl leading-relaxed">
          Créez une réservation en suivant <b>le même tunnel</b> qu&apos;un client sur le site.
          Chaque nouvelle offre publiée apparaît ici automatiquement, avec ses règles de
          disponibilité, de tarification et de paiement.
        </p>
      </div>

      {/* Info banner */}
      <div className="mb-8 bg-[#FAFAF7] border-l-2 border-[#B8922A] px-4 py-3 flex items-start gap-3 text-[0.78rem] text-[#0A0A0A]/70 leading-relaxed">
        <Info size={14} className="text-[#B8922A] mt-0.5 shrink-0" />
        <div>
          Cette réservation sera identique à une réservation client : elle apparaîtra dans la liste
          globale, mettra à jour les statistiques et décrémentera les disponibilités. Les billets
          / QR / emails sont générés selon les règles de l&apos;offre.
        </div>
      </div>

      {loading ? (
        <div className="text-[#0A0A0A]/55 text-sm py-12 text-center">Chargement du catalogue…</div>
      ) : !universe ? (
        <UniversePicker onPick={setUniverseId} offers={offers} events={events} />
      ) : (
        <div>
          <button
            onClick={() => setUniverseId(null)}
            className="text-[0.7rem] uppercase tracking-[0.28em] text-[#0A0A0A]/50 hover:text-[#B8922A] transition-colors inline-flex items-center gap-2 mb-6"
            data-testid="back-to-universes"
          >
            ← Changer d&apos;univers
          </button>
          <div className="mb-6">
            <div className="text-[0.6rem] uppercase tracking-[0.28em] text-[#B8922A]">
              {universe.label}
            </div>
            <h2 className="font-display-serif text-2xl md:text-3xl text-[#0A0A0A] mt-1">
              Choisir l&apos;offre
            </h2>
            <p className="text-sm text-[#0A0A0A]/55 mt-1">{universe.tagline}</p>
          </div>
          {displayedOffers.length === 0 ? (
            <div className="bg-white border border-dashed border-[#0A0A0A]/15 p-10 text-center text-sm text-[#0A0A0A]/55">
              Aucune offre disponible dans cet univers pour le moment.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="offers-grid">
              {displayedOffers.map((o) => (
                <OfferCard key={o.id} offer={o} onSelect={() => openTunnel(o)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
function UniversePicker({ onPick, offers, events }) {
  const countFor = (u) => {
    let n = offers.filter((o) => u.offer_ids.includes(o.id)).length;
    if (u.id === "beach_life") n += events.length;
    return n;
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="universes-grid">
      {UNIVERSES.map((u) => {
        const Icon = u.icon;
        const count = countFor(u);
        return (
          <button
            key={u.id}
            onClick={() => onPick(u.id)}
            className="group text-left bg-white border border-[#0A0A0A]/8 p-8 hover:border-[#B8922A] hover:shadow-[0_18px_40px_-24px_rgba(184,146,42,0.35)] transition-all duration-300"
            data-testid={`universe-pick-${u.id}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 border border-[#0A0A0A]/15 flex items-center justify-center group-hover:border-[#B8922A] group-hover:text-[#B8922A] transition-colors">
                <Icon size={18} />
              </div>
              <div className="flex-1">
                <div className="text-[0.6rem] uppercase tracking-[0.28em] text-[#B8922A]">
                  Univers
                </div>
                <div className="font-display-serif text-2xl text-[#0A0A0A]">{u.label}</div>
              </div>
              <ChevronRight size={16} className="text-[#0A0A0A]/35 group-hover:text-[#B8922A] group-hover:translate-x-1 transition-all" />
            </div>
            <p className="text-sm text-[#0A0A0A]/60 leading-relaxed">{u.tagline}</p>
            <div className="mt-5 pt-4 border-t border-[#0A0A0A]/8 text-[0.7rem] uppercase tracking-[0.22em] text-[#0A0A0A]/50">
              {count} offre{count > 1 ? "s" : ""} disponible{count > 1 ? "s" : ""}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
function OfferCard({ offer, onSelect }) {
  const Icon = OFFER_ICONS[offer.id?.startsWith("special_event") ? "special_event" : offer.id] || Ticket;
  const price = offer.price_adult || 0;
  const isPricedOnRequest = !price && !(offer.room_tiers || []).length;
  const hasTiers = (offer.room_tiers || []).length > 0;
  const tierMin = hasTiers
    ? Math.min(...offer.room_tiers.filter((t) => t.price).map((t) => t.price))
    : null;
  return (
    <button
      onClick={onSelect}
      className="group text-left bg-white border border-[#0A0A0A]/10 p-5 hover:border-[#B8922A] hover:shadow-[0_12px_28px_-20px_rgba(184,146,42,0.4)] transition-all duration-300 flex flex-col"
      data-testid={`offer-pick-${offer.id.replace(":", "-")}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 border border-[#0A0A0A]/15 flex items-center justify-center group-hover:border-[#B8922A] group-hover:text-[#B8922A] transition-colors shrink-0">
          <Icon size={15} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display-serif text-lg text-[#0A0A0A] leading-tight">
            {offer.name_fr || offer.id}
          </div>
          {offer.schedule_fr && (
            <div className="text-[0.7rem] text-[#0A0A0A]/55 mt-1">{offer.schedule_fr}</div>
          )}
        </div>
      </div>
      <div className="mt-auto pt-3 flex items-center justify-between">
        <div className="text-[0.72rem]">
          {isPricedOnRequest ? (
            <span className="text-[#0A0A0A]/55 uppercase tracking-[0.18em]">Sur demande</span>
          ) : hasTiers ? (
            <span className="text-[#0A0A0A]/70">À partir de <b className="text-[#0A0A0A]">{formatXOF(tierMin)}</b> / nuit</span>
          ) : (
            <span className="text-[#0A0A0A]/70">Adulte <b className="text-[#0A0A0A]">{formatXOF(price)}</b></span>
          )}
        </div>
        <span className="text-[0.6rem] uppercase tracking-[0.22em] text-[#B8922A] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          Ouvrir <ExternalLink size={11} />
        </span>
      </div>
    </button>
  );
}
